import { BaseLayer } from '../layers/BaseLayer.js';
import { Spiral, SpiralPoint } from '../utils/spiral.js';
import { easeOutQuart } from '../utils/easings.js';

/********************************/
/*   CLOUD SPIRAL LAYER CLASS   */
/********************************/

/**
 * Cloud spiral layer - renders an inward spiral of clouds for the background
 */
export class CloudSpiralLayer extends BaseLayer {
  constructor(width, height, density, cloudImage) {
    super(width, height, density, true);
    this.cloudImage = cloudImage;
  }

  setup() {
    // One-time setup for background
    // Spiral parameters
    const numClouds = 24; // Number of clouds in the spiral
    const maxRadius = this.width; // Maximum radius of the spiral
    const spiralTurns = 10; // Number of complete spiral turns
    const cloudSize = this.width * 1.2; // Size of each cloud

    // Create spiral with pre-calculated positions and angles
    this.spiral = new Spiral(numClouds, maxRadius, spiralTurns);

    // Create spiral point instances with custom properties
    this.spiralPoints = [];
    for (let i = 0; i < numClouds; i++) {
      this.spiralPoints.push(new SpiralPoint(this.spiral, i, {
        size: cloudSize
      }));
    }
  }

  draw() {
    // Clear the background
    clear();

    // Calculate rotation speed based on loop progress
    const spinSpeed = this.loopPercent * TWO_PI * 0.02; // Rotation speed

    imageMode(CENTER);

    // Draw one big cloud in the center and rotate it slowly to avoid holes in the background
    push();
    translate(0, 0);
    scale(3);
    rotate(-spinSpeed);
    image(this.cloudImage, 0, 0, this.width, this.height);
    pop();

    // Draw clouds in reversed order (innermost to outermost)
    for (let i = this.spiralPoints.length - 1; i >= 0; i--) {
      const state = this.spiralPoints[i].getState(spinSpeed);

      // Calculate angle pointing toward center (0, 0)
      const cloudPos = createVector(state.x, state.y);
      const toCenter = createVector(0, 0).sub(cloudPos);
      const angle = state.angleOffset + toCenter.heading();

      // Fade clouds as they approach the center using easing
      const easedT = easeOutQuart(state.t);
      const opacity = map(easedT, 1, 0, 1, 8);

      // Scale clouds smaller as they approach the center
      const scale = map(state.t, 0, 1, 1.2, 0.3);

      // Draw cloud image
      push();
      translate(state.x, state.y);
      rotate(angle);
      image(this.cloudImage, 0, 0, state.size * scale, state.size * scale);
      pop();

      // Add athmospheric perspective effect
      // Stacking multiple transparent layers to simulate depth
      push();
      blendMode(SCREEN);
      noStroke();
      fill(255, opacity);
      rect(-width / 2, -height / 2, width, height);
      pop();
    }

    noTint();
  }
}
